import type { CommercialTermsProductVariantInput,
  FixedProductInput,
  FixedProductVariantInput, InstallmentsProductVariantInput,
  PlanInputFormUsageProduct,
  PromotionInput,
  UsageProductVariantInput } from '@weft/types';
import { BILLING_INTERVALS, USER_INPUT_BILLING_INTERVALS } from '@weft/types';
import { MAX_TO, PAYMENT_TERMS, PRICING_MODELS, PROMOTION_TYPES } from '@weft/types';
import { newId } from '@weft/utils';

export const getDefaultFixedProduct = (): FixedProductInput => ({
  id: newId(),
  variants: [getDefaultFixedProductVariant()],
  paymentTerm: PAYMENT_TERMS.Postpayment,
  billingInterval: BILLING_INTERVALS.Monthly,
});

export const getDefaultFixedPerUnitProduct = (): FixedProductInput => ({
  id: newId(),
  variants: [getDefaultFixedPerUnitVariant()],
  paymentTerm: PAYMENT_TERMS.Postpayment,
  billingInterval: BILLING_INTERVALS.Monthly,
});

export const getDefaultCommissionProduct = (): FixedProductInput => ({
  id: newId(),
  variants: [getDefaultCommissionProductVariant()],
  paymentTerm: PAYMENT_TERMS.Postpayment,
  billingInterval: BILLING_INTERVALS.Monthly,
});

export const getDefaultSubscriptionProduct = (): FixedProductInput => ({
  id: newId(),
  variants: [getDefaultSubscriptionProductVariant()],
  paymentTerm: PAYMENT_TERMS.Postpayment,
  billingInterval: BILLING_INTERVALS.Monthly,
});

export const getDefaultCommercialTermsProduct = (): FixedProductInput => ({
  id: newId(),
  variants: [getDefaultCommercialTermsProductVariant()],
  paymentTerm: PAYMENT_TERMS.Prepayment,
  billingInterval: BILLING_INTERVALS.Yearly,
});

export const getDefaultInstallmentsProduct = (): FixedProductInput => ({
  id: newId(),
  variants: [getDefaultInstallmentsProductVariant()],
  paymentTerm: PAYMENT_TERMS.Postpayment,
  billingInterval: BILLING_INTERVALS.Monthly,
});

export const getDefaultRefundProduct = (): FixedProductInput => ({
  id: newId(),
  variants: [getDefaultRefundProductVariant()],
  paymentTerm: PAYMENT_TERMS.Postpayment,
  billingInterval: BILLING_INTERVALS.Monthly,
});

export const getDefaultTiersUsageProduct = (): PlanInputFormUsageProduct => ({
  id: newId(),
  variants: [getDefaultTiersUsageProductVariant()],
  paymentTerm: PAYMENT_TERMS.Postpayment,
  billingInterval: USER_INPUT_BILLING_INTERVALS.Inherit,
});

export const getDefaultTieredSubscriptionUsageProduct = (): PlanInputFormUsageProduct => ({
  id: newId(),
  variants: [getDefaultTieredSubscriptionUsageProductVariant()],
  paymentTerm: PAYMENT_TERMS.Postpayment,
  billingInterval: USER_INPUT_BILLING_INTERVALS.Inherit,
});

export const getDefaultRevShareUsageProduct = (): PlanInputFormUsageProduct => ({
  id: newId(),
  isRevShare: true,
  variants: [getDefaultTiersUsageProductVariant()],
  paymentTerm: PAYMENT_TERMS.Postpayment,
  billingInterval: USER_INPUT_BILLING_INTERVALS.Inherit,
});

export const getDefaultVolumeUsageProduct = (): PlanInputFormUsageProduct => ({
  id: newId(),
  variants: [getDefaultVolumeUsageProductVariant()],
  paymentTerm: PAYMENT_TERMS.Postpayment,
  billingInterval: USER_INPUT_BILLING_INTERVALS.Inherit,
});

export const getDefaultPerUnitUsageProduct = (): PlanInputFormUsageProduct => ({
  id: newId(),
  variants: [getDefaultPerUnitUsageProductVariant()],
  paymentTerm: PAYMENT_TERMS.Postpayment,
  billingInterval: USER_INPUT_BILLING_INTERVALS.Inherit,
});

export const getDefaultCreditPromotion = (): PromotionInput => ({
  id: newId(),
  promotionModel: {
    promotionType: PROMOTION_TYPES.Credit,
    promotionConfiguration: {
      creditAmount: null,
    },
  },
});

export function getDefaultPercentagePromotion(): PromotionInput {
  return {
    id: newId(),
    promotionModel: {
      promotionType: PROMOTION_TYPES.Percentage,
      promotionConfiguration: {
        percentage: null,
      },
    },
  };
}

export function getDefaultFreeTrialPromotion(): PromotionInput {
  return {
    id: newId(),
    promotionModel: {
      promotionType: PROMOTION_TYPES.FreeTrial,
      promotionConfiguration: {
        daysFree: null,
      },
    },
  };
}

export const getDefaultTiersUsageProductVariant = (): UsageProductVariantInput => ({
  id: newId(),
  pricing: {
    pricingModel: PRICING_MODELS.Tiers,
    priceConfiguration: {
      tiers: [
        { from: 1, to: MAX_TO, price: 0, flatRate: 0 },
      ],
    },
  },
});

export const getDefaultTieredSubscriptionUsageProductVariant = (): UsageProductVariantInput => ({
  id: newId(),
  pricing: {
    pricingModel: PRICING_MODELS.TieredSubscription,
    priceConfiguration: {
      tiers: [
        { from: 0, to: MAX_TO, price: 0, flatRate: 0 },
      ],
    },
  },
});

export const getDefaultVolumeUsageProductVariant = (): UsageProductVariantInput => ({
  id: newId(),
  pricing: {
    pricingModel: PRICING_MODELS.Tiers,
    priceConfiguration: {
      isVolume: true,
      tiers: [
        { from: 1, to: MAX_TO, price: 0, flatRate: 0 },
      ],
    },
  },
});

export const getDefaultPerUnitUsageProductVariant = (): UsageProductVariantInput => ({
  id: newId(),
  pricing: {
    pricingModel: PRICING_MODELS.PerUnit,
    priceConfiguration: {
      chunkSize: 1,
    },
  },
});

export const getDefaultFixedProductVariant = (): FixedProductVariantInput => ({
  id: newId(),
  pricing: {
    pricingModel: PRICING_MODELS.FixedOneTime,
  },
});

export const getDefaultRefundProductVariant = (): FixedProductVariantInput => ({
  id: newId(),
  pricing: {
    pricingModel: PRICING_MODELS.Refund,
  },
});

export const getDefaultFixedPerUnitVariant = (): FixedProductVariantInput => ({
  id: newId(),
  pricing: {
    pricingModel: PRICING_MODELS.FixedPerUnit,
  },
});

export const getDefaultCommissionProductVariant = (): FixedProductVariantInput => ({
  id: newId(),
  pricing: {
    pricingModel: PRICING_MODELS.Commission,
  },
});

export const getDefaultInstallmentsProductVariant = (): FixedProductVariantInput => ({
  id: newId(),
  pricing: {
    pricingModel: PRICING_MODELS.Installments,
  },
} satisfies InstallmentsProductVariantInput);

export const getDefaultSubscriptionProductVariant = (): FixedProductVariantInput => ({
  id: newId(),
  pricing: {
    pricingModel: PRICING_MODELS.Subscription,
  },
});

export const getDefaultCommercialTermsProductVariant = (): CommercialTermsProductVariantInput => ({
  id: newId(),
  pricing: {
    pricingModel: PRICING_MODELS.CommercialTerms,
  },
  description: 'Commercial terms',
});